/**
 * Rinnovo silenzioso della sessione dell'associato sul sito («rinnovo a 90», decisione del
 * committente del 25/10/2026). Il token del sito dura 7 giorni; qui lo si rinnova con
 * POST /auth/refresh-token:
 *  - all'avvio del sito (index.tsx): se il token è già scaduto ma rinnovabile si aspetta il
 *    rinnovo prima di disegnare la pagina (`prepara`), così nessun componente vede un token
 *    scaduto; se è valido ed emesso da più di un'ora si rinnova in sottofondo;
 *  - ogni 12 ore con la pagina aperta, e al ritorno sulla scheda se il token ha più di 12 ore
 *    (i timer delle schede in sottofondo e dei portatili sospesi non sono affidabili).
 * Il server rinnova anche un token scaduto da meno di 90 giorni dall'ultima emissione: chi apre
 * il sito almeno una volta ogni 90 giorni non rifà mai l'accesso. Mai impersonificazione.
 *
 * Esiti: rifiuto definitivo (401/403/404) di un token SCADUTO ⇒ logout (la sessione è finita);
 * di un token ancora valido ⇒ nulla (ci pensano la guardia dell'area riservata e, per il
 * dipendente, l'intercettore di fineSessioneDipendente: la richiesta porta il token in
 * Authorization apposta). 403 `STUDIO_NON_IN_REGOLA` del dipendente ⇒ la sessione resta (R16).
 * Rete assente o 5xx ⇒ nulla: si riprova al prossimo giro. Il token nuovo si salva solo se nel
 * frattempo nessuno ha cambiato quello salvato (logout, accesso di un'altra persona).
 */
import axios from 'axios';
import { getToken, saveToken, logout } from './otpAuthService';
import {
  leggiPayload, statoToken, daRinnovare, ETA_MINIMA_RINNOVO_MS, INTERVALLO_RINNOVO_MS,
} from '../utils/rinnovoSessione';
import { codiceDaRisposta } from '../utils/sessioneAssociato';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const TIMEOUT_RICHIESTA_MS = 15000;
/** Attesa massima del rinnovo prima del primo disegno della pagina. */
export const ATTESA_AVVIO_MS = 8000;

export type EsitoRinnovo = 'rinnovato' | 'saltato' | 'rifiutato' | 'indeterminato';

let inCorso: Promise<EsitoRinnovo> | null = null;

/**
 * Rinnova il token salvato se serve (`daRinnovare` con la soglia d'età indicata). Una sola
 * richiesta alla volta. Non lancia mai.
 */
export function rinnovaSessioneAssociato(etaMinimaMs: number = ETA_MINIMA_RINNOVO_MS): Promise<EsitoRinnovo> {
  if (inCorso) return inCorso;
  let token: string | null;
  try {
    token = getToken();
  } catch (e) {
    return Promise.resolve('saltato');
  }
  const payload = leggiPayload(token);
  if (!token || !daRinnovare(payload, Date.now(), etaMinimaMs)) return Promise.resolve('saltato');
  const eraScaduto = statoToken(payload) === 'rinnovabile';

  const richiesta: Promise<EsitoRinnovo> = Promise.resolve()
    .then(() => axios.post(
      `${API_BASE_URL}/auth/refresh-token`,
      { token },
      { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true, timeout: TIMEOUT_RICHIESTA_MS }
    ))
    .then((res): EsitoRinnovo => {
      if (!res || typeof res.status !== 'number') return 'indeterminato';
      if (getToken() !== token) return 'saltato';
      const nuovo = res.data?.data?.token;
      if (res.status >= 200 && res.status < 300) {
        if (typeof nuovo !== 'string' || !nuovo) return 'indeterminato';
        saveToken(nuovo);
        return 'rinnovato';
      }
      if (res.status === 403 && codiceDaRisposta(res.data) === 'STUDIO_NON_IN_REGOLA') return 'indeterminato';
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        if (eraScaduto) logout();
        return 'rifiutato';
      }
      return 'indeterminato';
    })
    .catch((): EsitoRinnovo => 'indeterminato')
    .finally(() => {
      if (inCorso === richiesta) inCorso = null;
    });
  inCorso = richiesta;
  return richiesta;
}

/**
 * Prima del primo disegno: se il token salvato è scaduto ma rinnovabile, lo rinnova e aspetta
 * (al più `attesaMs`). Negli altri casi risolve subito. Non lancia mai.
 */
export function preparaSessioneAssociato(attesaMs: number = ATTESA_AVVIO_MS): Promise<void> {
  let stato;
  try {
    stato = statoToken(leggiPayload(getToken()));
  } catch (e) {
    return Promise.resolve();
  }
  if (stato !== 'rinnovabile') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, attesaMs);
    rinnovaSessioneAssociato().then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

let avviato = false;

/**
 * Rinnovo all'apertura (token con più di un'ora), ogni 12 ore e al ritorno sulla scheda (token
 * con più di 12 ore). Una sola volta per pagina; restituisce la funzione che lo ferma (test).
 */
export function avviaRinnovoSessioneAssociato(): () => void {
  if (avviato || typeof window === 'undefined') return () => undefined;
  avviato = true;
  rinnovaSessioneAssociato(ETA_MINIMA_RINNOVO_MS);
  const intervallo = window.setInterval(() => {
    rinnovaSessioneAssociato(ETA_MINIMA_RINNOVO_MS);
  }, INTERVALLO_RINNOVO_MS);
  const allaVisibilita = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      rinnovaSessioneAssociato(INTERVALLO_RINNOVO_MS);
    }
  };
  document.addEventListener('visibilitychange', allaVisibilita);
  return () => {
    window.clearInterval(intervallo);
    document.removeEventListener('visibilitychange', allaVisibilita);
    avviato = false;
  };
}
